import { Container } from "@dojo/framework/core/Container";
import Injector from "@dojo/framework/core/Injector";
import { eventHandler } from "../../store/EventHandler";
import { getImageCount } from "../../store/PageHelper";
import { IReduxInjectorPayload } from "../../store/ReduxInjector";
import { CanvasStateConnectedEditor } from "../CanvasStateConnectedEditor";
import { IApiSet } from "../injectors/ApiContext";
import { CanvasMode } from "../interfaces/model/enumerations/index";
import { IBrowserType, ICanvasEditor, ICanvasEditorState, ISelection } from "../interfaces/model/index";
import { getCanvasMode } from "../selectors/canvasMode";
import { getSelectedPageObjects } from "../selectors/index";
import { getSelectedPage } from "../selectors/page";

/** save past selections for onSelectionChange emitter */
let lastSelectionKey = "";

/**
 * Container widget hook to provide data to widget.
 * The value returned by getProperties is an object containing two properties:
 * store - the actual Redux store passed as the first argument to the
 * ReduxInjector constructor.
 * extraOptions - an additional options object that can be passed as an optional
 * second argument to the ReduxInjector constructor.
 * An injector can be defined in the registry, which is then provided to the
 * Projector as one of its properties. This is demonstrated in the example below.
 * @param inject ReduxInjectorPayload<CanvasEditorState, any> the object
 * 		containing redux store and extraOptions provided to ReduxInjector
 * @param properties Current Page properties owned by CanvasPage
 */
function getProperties(injector: Injector<IReduxInjectorPayload<ICanvasEditorState, any>>) {
	const inject =  injector.get();
	const state: ICanvasEditorState = inject.store.getState();
	const browserType: IBrowserType = inject.apiContext.browserType;
	const dontDownloadImages: boolean = inject.apiContext.dontDownloadImages;
	const { emitImagesLoadedEvent } = inject.apiContext;
	const page = getSelectedPage(state);
	let imageCount = 0;
	if (page && emitImagesLoadedEvent) {
		if (!(imageCount = getImageCount(page))) {
			eventHandler.emit("onImagesLoaded", "No images to emit event for.");
		}
	}
	// passing callback as null, since we don't want callback for now.
	const canvasMode = getCanvasMode(state);
	const { present } = state.selection;
	const {
		defaultTextStyles,
		groupSelectionIndices,
		highlightType,
		impossiblePaths,
		selectedPageObjects,
		shapeDrawTools,
	} = present;
	const newSelectionKey = `${selectedPageObjects}_${groupSelectionIndices}_${shapeDrawTools?.shapeType}_${canvasMode}`;
	if (lastSelectionKey !== newSelectionKey && CanvasMode.EDIT_TEXT !== canvasMode) {
		eventHandler.emit("onSelectionChange");
	}
	lastSelectionKey = newSelectionKey;
	const selectedCurvePoint = state.canvasEditor.selectedCurvePoint;
	const api: IApiSet = inject.apiContext.api;
	const textStyles = present.formatPainterTextStyles;
	const canvasEditor: ICanvasEditor = state.canvasEditor;
	const pageProps = {
		api,
		canvasEditor,
		canvasMode,
		defaultTextStyles,
		dontDownloadImages,
		emitImagesLoadedEvent,
		groupSelectionIndices,
		highlightType,
		imageCount,
		impossiblePaths,
		page,
		selectedPageObjects,
		textStyles,
	};
	const selection: ISelection = present;
	const createShapeProps = { page, api, canvasEditor, selection };
	const selectedObjects = getSelectedPageObjects(state);
	const selectionOverlayProps = {
		api,
		browserType,
		canvasEditor,
		canvasMode,
		groupSelectionIndices,
		page,
		selectedCurvePoint,
		selectedObjects,
	};
	const guides = state.guides.guides;
	const guidesOverlayProps = { guides };
	return { pageProps, createShapeProps, selectionOverlayProps, guidesOverlayProps };
}

/**
 * Connect the redux store and CanvasPage using DOJO 2 Container widget and
 * redux injector available in registry with label 'canvasEditor-state'
 */
const CanvasEditorContainer = Container(CanvasStateConnectedEditor, "canvasEditor-state", { getProperties });

export default CanvasEditorContainer;
